/**
 * Shared timing and tuning constants for Matrix client services.
 */

// Matrix server configuration
export const MATRIX_SERVER_URL = process.env.REACT_APP_MATRIX_SERVER_URL || 'https://matrix.juicyhouse.com';

export const STOP_CLIENT_SETTLE_MS = 250;

/** Serialized stop/start + startClient to avoid overlapping SDK sync loops. */
export const SYNC_POLL_TIMEOUT_MS = 30000;

/** Polling fallback while waiting for first timeline events after sync. */
export const TIMELINE_WAIT_POLL_MS = 300;

/** Only zero the recovery counter after sync has stayed up this long (avoids perpetual flapping reset). */
export const RECOVERY_ATTEMPTS_RESET_AFTER_STABLE_MS = 60_000;

/** After a hard recovery failure, pause restart attempts to avoid infinite churn loops. */
export const HARD_FAILURE_COOLDOWN_MS = 60_000;

/** Cap unread room entries to avoid unbounded Map growth on huge accounts. */
export const ROOM_UNREAD_CACHE_MAX_ENTRIES = 500;

/** Spread hydrate batch start times slightly to reduce homeserver spikes. */
export const HYDRATE_BATCH_JITTER_MAX_MS = 300;

/** Wait for first timeline events after startClient. Sized for limited initial /sync, not CATCHUP. */
export const WAIT_INITIAL_TIMELINE_MS = 10000;

/**
 * Mobile browsers often suspend tabs and tear down sync transports while matrix-js-sdk still
 * reports SYNCING/PREPARED. `restartSync` skips work when state looks healthy + timeline ready;
 * after this long hidden, we force stop/start on return so chat UI recovers without a full reload.
 */
export const TAB_VISIBLE_FORCE_RECOVERY_AFTER_HIDDEN_MS = 30_000;

/**
 * After this long idle, drop `since` and run a limited initial /sync on the same client
 * (login speed) instead of CATCHUP. Uses last PREPARED/SYNCING time, not only tab-hide
 * time — mobile browsers often skip `visibilitychange:hidden` when freezing a tab.
 * This must not run while a 1:1 call is in progress (`restartSync` defers).
 */
export const MATRIX_RESUME_FRESH_SYNC_AFTER_HIDDEN_MS = 30 * 60 * 1000;

/**
 * Backend Matrix short-lived access token lifetime. Starting `/sync` with an expired
 * token waits on the 30s long-poll before 401, which is the inbox "Initializing..." hang.
 */
export const MATRIX_ACCESS_TOKEN_TTL_MS = 6 * 60 * 60 * 1000;

/** Refresh before expiry so returning users never boot Matrix with a token that 401s. */
export const MATRIX_ACCESS_TOKEN_REFRESH_MARGIN_MS = 60 * 60 * 1000;

/** Persist a compact inbox so the list can paint while a login-like re-sync runs. */
export const MATRIX_INBOX_SNAPSHOT_MAX_ROOMS = 120;
export const MATRIX_INBOX_SNAPSHOT_PERSIST_MS = 2000;

/** Survives iOS tab freeze when the JS heap is discarded. */
export const MATRIX_TAB_HIDDEN_AT_KEY = 'matrix_tab_hidden_at';

/** Last PREPARED/SYNCING (not CATCHUP). Used when hide events were missed. */
export const MATRIX_LAST_SUCCESSFUL_SYNC_AT_KEY = 'matrix_last_successful_sync_at';

/** If a better restart arrives mid-flight, run at most one extra stop/start. */
export const MATRIX_SYNC_RECOVERY_CHAIN_MAX = 2;

/**
 * Timeline events per room on inbox /sync. Keep small so creators with many DMs do not
 * download tens of thousands of events before the message list can render.
 */
export const MATRIX_LIST_SYNC_LIMIT = 20;

/**
 * Legacy Matrix-layer default; 1:1 chat paging uses MESSAGE_CONSTANTS.INITIAL_VISIBLE_MESSAGES.
 * Kept in sync with the chat page size so other callers do not silently reintroduce a 300-event dump.
 */
export const MATRIX_CHAT_BACKFILL_LIMIT = 30;

/**
 * After /sync, we optionally prime each room’s live timeline. This must stay small:
 * passing the full chat backfill limit here made `paginateRoomIfNeeded` aim for 2× that many
 * events per room and blocked app init with `Promise.all` across every room.
 * Opened chats still use `ensureRoomReady` / hook backfill for deeper history.
 */
export const MATRIX_POST_SYNC_HYDRATION_LIMIT = 36;

/** Post-sync hydration disabled — chat open uses ensureRoomReady / scrollback instead. */
export const HYDRATE_ROOMS_MAX = 0;

export const HYDRATE_ROOMS_CONCURRENCY = 5;

/** Per-room cap so one stuck pagination does not block an entire hydrate batch. */
export const HYDRATE_PAGINATE_TIMEOUT_MS = 5000;

/**
 * CallManager: after mount, poll the SDK for live calls (covers slow first sync or
 * Call.incoming emitted before the listener was attached). ~50 × 300ms ≈ 15s.
 */
export const CALL_HYDRATE_INITIAL_RETRIES = 50;
export const CALL_HYDRATE_INITIAL_INTERVAL_MS = 300;

/**
 * When Matrix timeline becomes ready (or again after sync recovery), short burst to
 * pick up m.call.* that just landed in the call event handler.
 */
export const CALL_HYDRATE_TIMELINE_READY_RETRIES = 24;
export const CALL_HYDRATE_TIMELINE_READY_INTERVAL_MS = 250;

/** Ignore duplicate endCall/reject for the same Matrix call id within this window (429 / SDK hangup race). */
export const VOIP_TERMINATE_DEBOUNCE_MS = 600;

/**
 * Coalesce m.call.sdp_stream_metadata_changed / org.matrix.call.sdp_stream_metadata_changed
 * (MSC3077) sends so Synapse room rate limits do not return M_LIMIT_EXCEEDED / 429 under
 * rapid mute/camera churn. Aligns with CS API rate limiting: Retry-After then retry_after_ms.
 */
export const CALL_METADATA_UPDATE_DEBOUNCE_MS = 450;
/** Upper bound so continuous toggles still flush (debounce starvation guard). */
export const CALL_METADATA_UPDATE_MAX_WAIT_MS = 1200;
/** Fallback when the server omits Retry-After / retry_after_ms (CS API). */
export const CALL_METADATA_UPDATE_DEFAULT_RETRY_MS = 2000;
/**
 * Cap Retry-After / retry_after_ms for background metadata retries only.
 * Mute awaiters resolve on 429 immediately (local media already applied), so this does not
 * need to fit inside callControlUtils SDK_MEDIA_CONTROL_TIMEOUT_MS (~1.8s).
 */
export const CALL_METADATA_UPDATE_MAX_RETRY_AFTER_MS = 10_000;
export const CALL_METADATA_UPDATE_MAX_RETRIES = 3;

/**
 * After CallManager start, delay incoming ringtone only while Matrix is still catching up
 * (hard refresh can replay a ringing invite, then hangup, in the next sync).
 * Live invites after this window — or when timeline is already ready — ring immediately.
 */
export const RINGTONE_BOOT_SETTLE_MS = 1600;

/** After timeline becomes ready, wait this long for a replayed hangup before ringing. */
export const RINGTONE_HANGUP_FOLLOW_MS = 400;
